/**
 * Sidebar Component
 * 1단 아코디언 구조 - 관리자/사용자 공통 컴포넌트, data-role 로 테마 분기
 * (이전 2단 구조는 Sidebar_before.tsx 참고)
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../../api';
import { isAdminRole } from '../../utils/role';
import { menuApi, MenuItem } from '../../api/menus';
import { SidebarIcon } from './sidebarIcons';

interface SubMenuItem {
  path: string;
  label: string;
}

interface NavItem {
  key: string;
  label: string;
  path?: string;
  subItems?: SubMenuItem[];
}

/** DDB 메뉴 데이터를 NavItem 구조로 변환 */
function buildNavItems(items: MenuItem[]): { main: NavItem[]; admin: NavItem[] } {
  const depth0 = items.filter((m) => m.depth === '0').sort((a, b) => a.sortOrder - b.sortOrder);
  const depth1 = items.filter((m) => m.depth === '1').sort((a, b) => a.sortOrder - b.sortOrder);

  const toNavItem = (m: MenuItem): NavItem => {
    const children = depth1.filter((c) => c.parentIdx === m.menuIdx);
    return {
      key: m.menuCd,
      label: m.menuNm,
      path: children.length === 0 ? m.menuUrl || undefined : undefined,
      subItems: children.length > 0
        ? children.map((c) => ({ path: c.menuUrl, label: c.menuNm }))
        : undefined,
    };
  };

  const main  = depth0.filter((m) => m.section === 'main').map(toNavItem);
  const admin = depth0.filter((m) => m.section === 'admin').map(toNavItem);
  return { main, admin };
}

/** URL이 메뉴 path와 매칭되는 점수 (높을수록 더 정확히 일치, 0이면 불일치) */
function matchScore(menuPath: string | undefined, pathname: string, search: string): number {
  if (!menuPath) return 0;
  const full = pathname + search;
  const base = menuPath.split('?')[0];
  if (menuPath === full) return 1000 + menuPath.length;   // 쿼리까지 정확히 일치
  if (base === pathname) return 500 + base.length;        // 경로 정확히 일치
  if (base !== '/' && pathname.startsWith(base + '/')) return 100 + base.length; // 하위 경로
  return 0;
}

/**
 * 현재 경로에 해당하는 1뎁스 메뉴 키 (DDB 메뉴 데이터 기반)
 * - 단일 메뉴: 자기 path 매칭
 * - 그룹 메뉴: 하위 subItems 중 하나라도 매칭
 * 가장 높은 점수의 메뉴 key를 반환 → 메뉴를 새로 추가해도 하이라이트 자동 동작
 */
function getActiveMenuKey(items: NavItem[], pathname: string, search: string): string | null {
  let bestKey: string | null = null;
  let bestScore = 0;

  for (const item of items) {
    // 단일 메뉴
    const selfScore = matchScore(item.path, pathname, search);
    if (selfScore > bestScore) { bestScore = selfScore; bestKey = item.key; }
    // 그룹 메뉴 하위
    for (const sub of item.subItems ?? []) {
      const subScore = matchScore(sub.path, pathname, search);
      if (subScore > bestScore) { bestScore = subScore; bestKey = item.key; }
    }
  }
  return bestKey;
}

/** 서브메뉴 중 현재 URL과 가장 잘 맞는 path (정확히 일치 → 가장 긴 prefix 순) */
function findActiveSubPath(subs: SubMenuItem[], pathname: string, search: string): string | null {
  const full = pathname + search;
  const exact = subs.find((s) => s.path === full) ?? subs.find((s) => s.path === pathname);
  if (exact) return exact.path;
  const prefix = subs
    .filter((s) => s.path && pathname.startsWith(s.path.split('?')[0] + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return prefix?.path ?? null;
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authApi.getUser();
  const role = isAdminRole(user?.role) ? 'admin' : 'user';

  // DDB에서 로드된 메뉴
  const [mainNavItems, setMainNavItems] = useState<NavItem[]>([]);
  const [adminNavItems, setAdminNavItems] = useState<NavItem[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  // 접힘 상태 플라이아웃 (nav 가 overflow 라서 fixed 좌표로 띄움)
  const [flyout, setFlyout] = useState<{ key: string; top: number; left: number } | null>(null);

  const activeMenu = getActiveMenuKey(
    [...mainNavItems, ...adminNavItems],
    location.pathname,
    location.search,
  );

  useEffect(() => {
    loadMenus();

    const handleMenuChange = () => loadMenus();
    window.addEventListener('menu-changed', handleMenuChange);
    return () => window.removeEventListener('menu-changed', handleMenuChange);
  }, []);

  // 현재 경로의 그룹은 자동으로 펼침 (다른 그룹은 닫음 - 한 번에 하나만)
  useEffect(() => {
    if (!activeMenu) return;
    setExpanded((prev) => (prev.size === 1 && prev.has(activeMenu) ? prev : new Set([activeMenu])));
  }, [activeMenu]);

  // 프로필 팝업 바깥 클릭 시 닫기
  useEffect(() => {
    if (!profileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  // 플라이아웃 영역(메뉴 아이템 + 팝업) 바깥 클릭 시 닫기 (터치 등 mouseleave 없는 경우 대응)
  useEffect(() => {
    if (!flyout) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.sb-group')) setFlyout(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [flyout]);

  const loadMenus = async () => {
    try {
      const items = await menuApi.getMenus('ko');
      const { main, admin } = buildNavItems(items);
      setMainNavItems(main);
      setAdminNavItems(admin);
    } catch {
      // 실패 시 빈 메뉴 유지
    }
  };

  const handleLogout = () => {
    authApi.logout();
    navigate('/login');
  };

  const openUserGuide = () => {
    window.open('/html/acxion_manual.html', '_blank');
  };

  // 아코디언: 그룹을 펼치면 이미 펼쳐진 다른 그룹은 닫힘, 같은 그룹을 다시 누르면 닫힘
  const toggleGroup = (key: string) => {
    setExpanded((prev) => (prev.has(key) ? new Set() : new Set([key])));
  };

  const openFlyout = (key: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setFlyout({ key, top: rect.top, left: rect.right });
  };

  const handleItemClick = (item: NavItem, e: React.MouseEvent<HTMLButtonElement>) => {
    if (item.subItems && item.subItems.length > 0) {
      // 접힌 상태에서는 클릭으로도 플라이아웃 열기 (호버로 이미 열려 있으면 유지)
      if (collapsed) {
        openFlyout(item.key, e.currentTarget.parentElement!);
        return;
      }
      toggleGroup(item.key);
      return;
    }
    // 접힌 상태에서는 단일 메뉴도 클릭 시 타이틀 플라이아웃 유지 (영역 벗어나면 닫힘)
    if (collapsed) openFlyout(item.key, e.currentTarget.parentElement!);
    else setFlyout(null);
    if (item.key === 'userPortal' && item.path) {
      window.open(item.path, '_blank');
      return;
    }
    if (item.key === 'chat') {
      navigate(item.path || '/chat');
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  const renderItem = (item: NavItem) => {
    const hasSub = !!item.subItems && item.subItems.length > 0;
    const isCurrent = activeMenu === item.key;
    const isOpen = hasSub && !collapsed && expanded.has(item.key);
    const activeSubPath = hasSub && isCurrent
      ? findActiveSubPath(item.subItems!, location.pathname, location.search)
      : null;

    const showFlyout = collapsed && flyout?.key === item.key;

    return (
      <li
        key={item.key}
        className="sb-group"
        onMouseEnter={collapsed ? (e) => openFlyout(item.key, e.currentTarget) : undefined}
        onMouseLeave={collapsed ? () => setFlyout(null) : undefined}
      >
        <button
          type="button"
          className={[
            'sb-item',
            !hasSub && isCurrent ? 'active' : '',
            hasSub && isCurrent ? 'current' : '',
            isOpen ? 'open' : '',
          ].join(' ')}
          onClick={(e) => handleItemClick(item, e)}
          aria-label={collapsed ? item.label : undefined}
          aria-expanded={hasSub ? (collapsed ? showFlyout : isOpen) : undefined}
        >
          <span className="sb-item-icon"><SidebarIcon name={item.key} /></span>
          <span className="sb-item-label">{item.label}</span>
          {hasSub && (
            <span className="sb-item-chevron"><SidebarIcon name="chevronDown" /></span>
          )}
        </button>

        {isOpen && (
          <ul className="sb-sub">
            {item.subItems!.map((sub) => (
              <li key={sub.path || sub.label}>
                <button
                  type="button"
                  className={`sb-sub-item ${sub.path === activeSubPath ? 'active' : ''}`}
                  disabled={!sub.path}
                  onClick={() => sub.path && navigate(sub.path)}
                >
                  {sub.label}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* 접힘 상태 플라이아웃: 그룹은 타이틀 + 하위메뉴, 단일 메뉴는 타이틀만 */}
        {showFlyout && (
          <div className="sb-flyout" style={{ top: flyout!.top, left: flyout!.left }}>
            <div className="sb-flyout-card">
              <div className={`sb-flyout-title ${hasSub ? 'has-sub' : ''}`}>{item.label}</div>
              {hasSub && (
                <ul className="sb-flyout-list">
                  {item.subItems!.map((sub) => (
                    <li key={sub.path || sub.label}>
                      <button
                        type="button"
                        className={`sb-flyout-item ${sub.path === activeSubPath ? 'active' : ''}`}
                        disabled={!sub.path}
                        onClick={() => {
                          if (!sub.path) return;
                          navigate(sub.path);
                          setFlyout(null);
                        }}
                      >
                        {sub.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </li>
    );
  };

  const displayName = user?.name || (role === 'admin' ? '관리자' : '사용자');

  return (
    <aside className={`sb ${collapsed ? 'collapsed' : ''}`} data-role={role}>
      {/* 상단: 햄버거 + 로고 */}
      <div className="sb-brand">
        <button
          type="button"
          className="sb-toggle"
          onClick={() => {
            setCollapsed((v) => !v);
            setFlyout(null);
          }}
          aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
        >
          <SidebarIcon name="menu" />
        </button>
        {/* 로고 클릭 시 대시보드로 이동 (관리자/사용자 공통) */}
        <div
          className="sb-brand-text"
          role="link"
          tabIndex={0}
          onClick={() => navigate('/home')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/home');
            }
          }}
        >
          {/* 서비스 로고 (관리자 = 보라 심볼, 사용자 = 청록 심볼 / 글자는 공통) */}
          <img
            className="sb-logo-img"
            src={role === 'admin' ? '/logo-acxionon-admin.svg' : '/logo-acxionon-user.svg'}
            alt="Axcion-On"
          />
        </div>
      </div>

      {/* 메뉴 */}
      <nav className="sb-nav" onScroll={flyout ? () => setFlyout(null) : undefined}>
        <ul className="sb-list">
          {mainNavItems.map(renderItem)}
        </ul>
        {adminNavItems.length > 0 && (
          <>
            <div className="sb-divider" />
            <ul className="sb-list">
              {adminNavItems.map(renderItem)}
            </ul>
          </>
        )}
      </nav>

      {/* POWERED BY 로고 (admin 은 보라 배경이라 흰색 로고) */}
      <div className="sb-powered">
        <img
          className="sb-powered-logo"
          src={role === 'admin' ? '/logo-etech-white.svg' : '/logo-etech.svg'}
          alt="ETECH SYSTEM"
        />
      </div>

      {/* 하단: 프로필 + 팝업 */}
      <div className="sb-profile" ref={profileRef}>
        {profileOpen && (
          <div className="sb-profile-menu" role="menu">
            {/* TODO: 언어/테마 전환 기능 연결 */}
            <button type="button" className="sb-profile-menu-item" role="menuitem">
              <SidebarIcon name="globe" />
              <span>언어변경</span>
              <span className="sb-profile-menu-value">EN</span>
            </button>
            <button type="button" className="sb-profile-menu-item" role="menuitem">
              <SidebarIcon name="darkmode" />
              <span>화면테마</span>
              <span className="sb-profile-menu-value">라이트</span>
            </button>
            <button type="button" className="sb-profile-menu-item" role="menuitem" onClick={openUserGuide}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '1em', height: '1em' }}>
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
              <span>사용자 가이드</span>
            </button>
            <button type="button" className="sb-profile-menu-item" role="menuitem" onClick={handleLogout}>
              <SidebarIcon name="logout" />
              <span>로그아웃</span>
            </button>
          </div>
        )}

        <div className="sb-profile-row">
          <button
            type="button"
            className="sb-avatar"
            onClick={() => setProfileOpen((v) => !v)}
            title={collapsed ? displayName : undefined}
            tabIndex={collapsed ? 0 : -1}
          >
            {displayName[0]}
          </button>
          <div className="sb-profile-info">
            <span className="sb-profile-name">{displayName}</span>
            <span className="sb-profile-email">{user?.email}</span>
          </div>
          {/* <button
            type="button"
            className={`sb-profile-toggle ${profileOpen ? 'open' : ''}`}
            onClick={() => setProfileOpen((v) => !v)}
            aria-label="사용자 메뉴"
            aria-expanded={profileOpen}
          >
            <SidebarIcon name="chevronUp" />
          </button> */}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
